import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { getFormattedUserName } from "helpers/chats";
import { parseDate } from "../parseDate";
import DeleteModal from "components/PopupForms/DeleteDialog/DeleteModal";
import TooltipButton from "components/common/InfoPopup/TooltipButton";
import { ICON } from "components/common/IconButton";
import { GROUP_ROLES } from "constants/user";
import { ForumMessage, ForumReplyData, ForumEditData, DeleteModalData } from "types/forum";

interface ForumMessageCardProps extends ForumMessage {
    setReply: (data: ForumReplyData) => void;
    onEdit: (data: ForumEditData) => void;
    onDelete: (id: number, closeCallback: () => void) => void;
}

const isMessageEdited = (createdAt: string, updatedAt?: string): boolean => {
    if (!updatedAt) return false;
    const created = new Date(createdAt).getTime();
    const updated = new Date(updatedAt).getTime();
    return updated - created > 1000;
};

const ForumMessageCard: React.FC<ForumMessageCardProps> = ({
    firstname,
    middlename,
    lastname,
    reply_firstname,
    reply_middlename,
    reply_lastname,
    organization_name,
    message,
    created_at,
    updated_at,
    deleted_at,
    picture,
    file_url,
    id,
    is_reply,
    reply_message,
    reply_deleted_at,
    setReply,
    user_id,
    onEdit,
    onDelete,
}) => {
    const { t } = useTranslation();
    const authenticated = useSelector((state: any) => state.auth.authenticated);
    const currentUserId = useSelector((state: any) => state.profile.profileData?.id);
    const groupRoleId = useSelector((state: any) => state.profile.profileData?.group_role_id);
    const isAdmin = groupRoleId === GROUP_ROLES.ADMIN;
    const isOwnMessage = currentUserId === user_id;
    const canEdit = (isOwnMessage || isAdmin) && !deleted_at;
    const canDelete = (isOwnMessage || isAdmin) && !deleted_at;
    const isDeleted = !!deleted_at;
    const isEdited = isMessageEdited(created_at, updated_at);

    const userName = getFormattedUserName({
        firstname,
        middlename,
        lastname,
    });

    const replyName = getFormattedUserName({
        firstname: reply_firstname,
        middlename: reply_middlename,
        lastname: reply_lastname,
    });

    const replyToName = getFormattedUserName({
        firstname,
        middlename,
        lastname,
    });

    const deleteModalData: DeleteModalData = {
        title: t("forums.topic.delete_message_title"),
        id,
        name: "",
        type: t("forums.topic.message_type"),
    };

    return (
        <li className="forum-ticket__card">
            <div className="forum-ticket__user">
                <span
                    className="forum-ticket__avatar"
                    style={picture ? { backgroundImage: `url("${picture}")` } : undefined}
                />
                <p className="forum-ticket__name">{userName}</p>
                <p className="forum-ticket__company">{organization_name || ""}</p>
            </div>
            <div className={`forum-ticket__container${isDeleted ? " forum-ticket__container--deleted" : ""}`}>
                <div className="forum-ticket__content-row">
                    <div className="forum-ticket__messages-container">
                        {isDeleted ? (
                            <p className="forum-ticket__message forum-ticket__message--deleted">
                                {t("forums.topic.message_deleted_by_author")}
                            </p>
                        ) : (
                            <>
                                {is_reply && (
                                    <details className="forum-ticket__reply-container">
                                        <summary>
                                            {t("forums.topic.if_answer_text")} <b>{replyName}</b>
                                        </summary>
                                        <div>
                                            {reply_deleted_at ? (
                                                <p className="forum-ticket__reply-message forum-ticket__message--deleted">
                                                    {t("forums.topic.message_deleted_by_author")}
                                                </p>
                                            ) : (
                                                reply_message?.split("\n").map((el, index) => (
                                                    <p key={index} className="forum-ticket__reply-message">
                                                        {el || ""}
                                                    </p>
                                                ))
                                            )}
                                        </div>
                                    </details>
                                )}

                                {message?.split("\n").map((el, index) => (
                                    <p key={index} className="forum-ticket__message">
                                        {el || <br />}
                                    </p>
                                ))}
                                {file_url && <a href={file_url}>{t("forums.topic.document")}</a>}
                            </>
                        )}
                    </div>
                    {authenticated && (canEdit || canDelete) && (
                        <div className="forum-ticket__actions">
                            {canEdit && (
                                <TooltipButton
                                    hintText={t("edit")}
                                    icon={ICON.edit}
                                    onClick={() => onEdit({ id, message })}
                                />
                            )}
                            {canDelete && (
                                <DeleteModal
                                    data={deleteModalData}
                                    onDeleteClick={onDelete}
                                />
                            )}
                        </div>
                    )}
                </div>
                {authenticated && !isDeleted && (
                    <span className="forum-ticket__footer">
                        <span className="forum-ticket__date">
                            {isEdited
                                ? <>{parseDate(updated_at!)} <span className="forum-ticket__edited">({t("forums.topic.edited")})</span></>
                                : (created_at ? parseDate(created_at) : "-")
                            }
                        </span>
                        <button
                            className="forum-ticket__reply"
                            type="button"
                            onClick={() => setReply({ message, id, replyToName })}
                        >
                            {t("forums.topic.answer_button")}
                        </button>
                    </span>
                )}
                {isDeleted && (
                    <span className="forum-ticket__footer">
                        <span className="forum-ticket__date">
                            {created_at ? parseDate(created_at) : "-"}
                        </span>
                    </span>
                )}
            </div>
        </li>
    );
};

export default ForumMessageCard;
